import React, { useEffect, useState } from "react";
import { Table, Row, Spin } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { buildQueryString, exportFile, formatNumber } from "../../../utils/utilities";
import { setFilters } from "../../../redux/ducks/Filters";
import Filter from "../../../components/functional_components/Filter/Filter";
import { useFormik } from "formik";
import { CheckOutlined, CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import { showError, showSuccess } from "../../../utils/utilities";
import WorkProgressDm from "../../../services/api/WorkProgressDm";
import CostCentre from "../../../services/api/CostCentre";
import Supplier from "../../../services/api/Supplier";

const ReportDm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const [state, setState] = useState({
    dataContent: [],
    costCenterFilter: [],
    suppliersOptionsList: [],
    loading: false,
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString({}, {}); // Default querystring have pagination, but here I don't need pagination
    dispatch(setFilters(queryString));
    dataList(queryString.string);
    getCostCentre(); // Get cost centers for filter
  }, []);

  const dataList = async (queryString) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const report = await WorkProgressDm.getDmReport(queryString);
    if (!report.error && report.data && report.data.orders) {
      report.data.orders.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row and I use it for open row table
      });
      setState((prevState) => ({
        ...prevState,
        dataContent: get(report, "data.orders", []),
      }));
    } else {
      showError(get(report, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const getCostCentre = async () => {
    let dataforSelect = [];
    const cf = await CostCentre.getCostCentreFilter();
    if (!cf.error && cf.data) {
      cf.data.forEach((cc) => {
        dataforSelect.push({
          label: `${cc.id} ${cc.name !== null ? ` | ${cc.name}` : ""}`,
          value: cc.id,
        });
      });
      setState((prevState) => ({
        ...prevState,
        costCenterFilter: dataforSelect,
      }));
    } else {
      showError(get(cf, "error.messageCode", "ER000"));
    }
  };

  const resetFilter = () => {
    const queryString = buildQueryString({}, {}); // Default querystring have pagination, but here I don't need pagination
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const columns = [
    {
      title: t("report.table.oda"),
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("report.table.productCategoryId"),
      dataIndex: "productCategoryId",
      key: "productCategoryId",
      responsive: ["lg"],
    },
    {
      title: t("report.table.hourlyRate"),
      dataIndex: "hourlyRate",
      key: "hourlyRate",
      className: "table-value-right",
      render: (value, row) => {
        let hourlyRate = "";
        if (row.productCategoryType === "HOUR") {
          hourlyRate = formatNumber({
            value,
            lang: currentLanguage,
            suffix: row.productCategoryType,
          });
        }
        return hourlyRate;
      },
      responsive: ["lg"],
    },
    {
      title: t("report.table.totalOrder"),
      dataIndex: "totalOrder",
      key: "totalOrder",
      className: "table-value-right",
      render: (value, row) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: row.productCategoryType,
        }),
      responsive: ["lg"],
    },
    {
      title: t("report.table.receivedAmount"),
      dataIndex: "receivedAmount",
      key: "receivedAmount",
      className: "table-value-right",
      render: (value, row) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: row.productCategoryType,
        }),
      responsive: ["lg"],
    },
    {
      title: t("report.table.residualAmount"),
      dataIndex: "residualAmount",
      key: "residualAmount",
      className: "table-value-right",
      render: (value, row) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: row.productCategoryType,
        }),
      responsive: ["lg"],
    },
    {
      title: t("report.table.netValue"),
      dataIndex: "netValue",
      key: "netValue",
      className: "table-value-right",
      render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
      responsive: ["sm"],
    },
    {
      title: t("report.table.receivedValue"),
      dataIndex: "receivedValue",
      key: "receivedValue",
      className: "table-value-right",
      render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
    },
    {
      title: t("report.table.residualValue"),
      dataIndex: "residualValue",
      key: "residualValue",
      className: "table-value-right",
      render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
      responsive: ["sm"],
    },
  ];

  const expandedRowRender = (dataRow) => {
    const columns = [
      {
        title: t("report.table.project"),
        dataIndex: "projectId",
        key: "projectId",
        render: (value, row) => {
          return (
            <div>
              {value} | {row.projectName}
            </div>
          );
        },
        responsive: ["sm"],
      },
      {
        title: t("report.table.projectTask"),
        dataIndex: "projectTaskId",
        key: "projectTaskId",
        render: (value, row) => {
          return (
            <div>
              {value} | {row.projectTaskName}
            </div>
          );
        },
      },
      {
        title: t("report.table.receivedAmount"),
        dataIndex: "receivedAmount",
        key: "receivedAmount",
        className: "table-value-right",
        render: (value, row) =>
          formatNumber({
            value,
            lang: currentLanguage,
            suffix: row.productCategoryType,
          }),
        responsive: ["sm"],
      },
      {
        title: t("report.table.receivedValue"),
        dataIndex: "receivedValue",
        key: "receivedValue",
        className: "table-value-right",
        render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
        responsive: ["sm"],
      },
      {
        title: t("report.table.projectStatus"),
        dataIndex: "projectStatus",
        key: "projectStatus",
        responsive: ["lg"],
      },
      {
        title: t("report.table.projectTaskStatus"),
        dataIndex: "projectTaskStatus",
        key: "projectTaskStatus",
        responsive: ["lg"],
      },
      {
        align: "center",
        title: t("report.table.enabled"),
        dataIndex: "enabled",
        key: "enabled",
        render: (value) =>
          value ? <CheckOutlined className="icon-medium" /> : <CloseOutlined className="icon-medium" />,
        responsive: ["lg"],
      },
    ];

    const dataSource = dataRow.projectTasks.map((task, index) => ({
      ...task,
      key: index, // Assegna una chiave unica a ciascun task
    }));

    return (
      <>
        <Table columns={columns} dataSource={dataSource} pagination={false} />
      </>
    );
  };

  const formikFilter = useFormik({
    initialValues: {
      orderId: "",
      costCentreId: "",
      supplierId: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      const queryString = buildQueryString(filters.obj, values);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const changeSupplierFilter = (value) => {
    formikFilter.setFieldValue("supplierId", value);
    if (value === undefined) {
      setState((prevState) => ({ ...prevState, suppliersOptionsList: [] }));
    }
  };

  const onSearchSupplierFilter = async (value) => {
    if (value.length >= 3) {
      let dataopt = [];
      const suppliers = await Supplier.getSuppliers(`name=${value}&allPages=true`);
      if (!suppliers.error && suppliers.data) {
        suppliers.data.forEach((c) => {
          dataopt.push({ value: c.id, label: c.name });
        });
        setState((prevState) => ({
          ...prevState,
          suppliersOptionsList: dataopt,
        }));
      } else {
        showError(get(suppliers, "error.messageCode", "ER000"));
      }
    } else {
      setState((prevState) => ({ ...prevState, suppliersOptionsList: [] }));
    }
  };

  const fieldsFilter = [
    {
      type: "input",
      isRequired: false,
      id: "orderId",
      name: "orderId",
      label: t("report.filter.orderId"),
      value: formikFilter.values.orderId,
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "select",
      isRequired: false,
      id: "costCentreId",
      name: "costCentreId",
      value: formikFilter.values.costCentreId,
      label: t("report.filter.costCenter"),
      onChange: formikFilter.handleChange("costCentreId"),
      onBlur: formikFilter.handleBlur("costCentreId"),
      dataSelect: state.costCenterFilter,
    },
    {
      type: "selectWithSearch",
      allowClear: true,
      isRequired: false,
      id: "supplierId",
      name: "supplierId",
      value: formikFilter.values.supplierId,
      label: t("report.filter.suppliers"),
      onChange: changeSupplierFilter,
      onBlur: formikFilter.handleBlur,
      onSearch: onSearchSupplierFilter,
      dataSelect: state.suppliersOptionsList,
    },
  ];

  const exportReportCsv = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await WorkProgressDm.downloadDmReport(filters.string);
    if (!exportResponse.error) {
      const blob = new Blob([exportResponse]);
      exportFile(blob, "report.csv");
      showSuccess();
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  return (
    <>
      {state.loading ? (
        <div className="container-spinner-positioning">
          <Spin spinning={state.loading} className="spinner-positioning" />
        </div>
      ) : (
        ""
      )}
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} allInOneRow={true} />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={exportReportCsv}
        />
      </Row>
      <Table
        pagination={false}
        columns={columns}
        expandable={{ expandedRowRender }}
        dataSource={state.dataContent}
        size="small"
      />
    </>
  );
};

export default ReportDm;
